// ══════════════════════════════════════════════════════════════
// ESTADO DE RECOMPENSAS DEL USUARIO (agregado 06/10/2026)
//
// Única fuente de verdad: Usuarios/{uid}.Recompensas en Firestore.
//
// Antes "reclamado" y "equipado" se guardaban en localStorage del
// navegador (battlePassClaimedRewards / battlePassEquippedRewards /
// userAvatar). Eso causaba dos bugs reportados:
//   1. Lo reclamado en la compu aparecía sin reclamar en el celular.
//   2. Una vez reclamado, un cosmético no se podía volver a equipar,
//      así que el usuario quedaba con el último que había reclamado.
//
// Estas funciones arman el array completo que se escribe con
// UpdateUserRewardsState(), partiendo de mergeRewardsWithUserState().
// ══════════════════════════════════════════════════════════════

import type { UserRewardState } from "../firebase-config";
import { isRewardEquipable, NormalizedReward } from "./rewards";

const LEGACY_CLAIMED_KEY = "battlePassClaimedRewards";
const LEGACY_EQUIPPED_KEY = "battlePassEquippedRewards";

/**
 * Cosméticos de la misma ranura se excluyen entre sí: solo puede haber
 * una insignia y un cosmético de avatar equipados a la vez.
 */
export type EquipSlot = "badge" | "avatar";

export const getEquipSlot = (reward: NormalizedReward): EquipSlot =>
  reward.category === "badge" ? "badge" : "avatar";

const toUserState = (rewards: NormalizedReward[]): UserRewardState[] =>
  rewards.map((reward) => ({
    RewardId: reward.id,
    Claimed: reward.claimed,
    Equipped: reward.equipped,
  }));

/**
 * Equipa (o quita) una recompensa. Al equipar, se quita cualquier otra
 * de la misma ranura; las de la otra ranura no se tocan.
 */
export const buildEquipState = (
  rewards: NormalizedReward[],
  rewardId: string,
  shouldEquip: boolean,
): UserRewardState[] => {
  const target = rewards.find((reward) => reward.id === rewardId);
  if (!target) return toUserState(rewards);

  const slot = getEquipSlot(target);

  return rewards.map((reward) => {
    let equipped = reward.equipped;

    if (reward.id === rewardId) {
      equipped = shouldEquip;
    } else if (
      shouldEquip &&
      isRewardEquipable(reward) &&
      getEquipSlot(reward) === slot
    ) {
      equipped = false;
    }

    return { RewardId: reward.id, Claimed: reward.claimed, Equipped: equipped };
  });
};

/**
 * Marca recompensas como reclamadas. Si se pasa equipId, además la equipa
 * (es lo que pasa al reclamar un cosmético: se equipa solo).
 */
export const buildClaimState = (
  rewards: NormalizedReward[],
  rewardIds: string[],
  equipId?: string,
): UserRewardState[] => {
  const ids = new Set(rewardIds);
  const withClaims = rewards.map((reward) =>
    ids.has(reward.id) ? { ...reward, claimed: true } : reward,
  );

  return equipId
    ? buildEquipState(withClaims, equipId, true)
    : toUserState(withClaims);
};

// ---------- Migración de datos viejos de localStorage ----------

/**
 * Ids que el usuario reclamó con la versión anterior y que solo
 * quedaron guardados en este navegador. Devuelve [] si no hay nada.
 */
export const readLegacyClaimedRewardIds = (): string[] => {
  try {
    const raw = localStorage.getItem(LEGACY_CLAIMED_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.map((id) => String(id).trim()).filter(Boolean)
      : [];
  } catch {
    return [];
  }
};

/** Borra las claves viejas para que no vuelvan a pisar a Firestore. */
export const clearLegacyRewardStorage = () => {
  try {
    localStorage.removeItem(LEGACY_CLAIMED_KEY);
    localStorage.removeItem(LEGACY_EQUIPPED_KEY);
  } catch {
    // localStorage puede no estar disponible (modo privado): no es crítico.
  }
};
